import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Shield, Users, Award, Eye } from "lucide-react";
import { useLang } from "@/context/LangContext";

const ABOUT_IMG = "https://images.unsplash.com/photo-1758691736933-bb0f88fe2e0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjBtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzc1NDY0MjcyfDA&ixlib=rb-4.1.0&q=85";

const standards = [
  { name: "ISO/IEC 27001", desc: "Information Security Management" },
  { name: "ISO 9001", desc: "Quality Management Systems" },
  { name: "ISO 22301", desc: "Business Continuity Management" },
  { name: "ISO 27701", desc: "Privacy Information Management" },
  { name: "NIST CSF", desc: "Cybersecurity Framework" },
  { name: "SOC 2 Readiness", desc: "Service Organization Controls" },
];

const defaultDifferentiators = [
  "2-in-1 Security & Certification approach",
  "90-day certification readiness program",
  "IAF-aligned certifications through accredited partners",
  "24/7 Security Operations Center",
  "Dedicated lead auditor per engagement",
  "Cybersecurity vulnerability assessment included",
];

const defaultValues = [
  { title: "Security-First Approach", desc: "Cybersecurity expertise integrated into every certification engagement, ensuring management systems are both compliant and resilient." },
  { title: "Accredited Partner Network", desc: "Certification delivered through internationally accredited partner bodies, ensuring global recognition and acceptance." },
  { title: "Expert Auditors", desc: "Certified lead auditors with deep technical backgrounds in information security, risk management, and compliance." },
  { title: "Independent & Impartial", desc: "Strict independence and impartiality in all certification decisions, ensuring credible and trustworthy outcomes." },
];

function HeroSection({ a }) {
  return (
    <section className="bg-[#0B2545] py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#13A8BD] mb-4">{a.badge || "About Us"}</p>
        <h1 data-testid="about-page-heading" className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white leading-[1.1] mb-6" style={{ fontFamily: "Chivo, sans-serif" }}>
          {a.title || "About GACA International"}
        </h1>
        <p className="text-base md:text-lg text-white/65 max-w-2xl leading-relaxed">
          {a.subtitle || "Global Assurance & Certification Authority — an independent certification board founded by experienced cybersecurity and compliance leaders."}
        </p>
      </div>
    </section>
  );
}

function MissionSection({ a }) {
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
          <div className="lg:col-span-2">
            <img src={ABOUT_IMG} alt="GACA International" className="w-full h-80 object-cover mb-10" data-testid="about-image" />
            <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#0B2545] mb-6" style={{ fontFamily: "Chivo, sans-serif" }}>
              {a.body_title || "We Don't Just Certify — We Secure"}
            </h2>
            <div className="space-y-5 text-sm text-slate-600 leading-relaxed">
              <p>{a.body_p1 || "GACA International (Global Assurance & Certification Authority) is an independent certification board founded by experienced cybersecurity and compliance leaders."}</p>
              <p>{a.body_p2 || "Unlike traditional certification bodies, GACA integrates cybersecurity assessment capabilities into every certification engagement."}</p>
              <p>{a.body_p3 || "Our team of certified lead auditors and cybersecurity professionals brings decades of combined experience across financial services, technology, government, and critical infrastructure sectors."}</p>
            </div>
            <div className="mt-10 border border-[#0B2545]/10 p-8">
              <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-5">Key Differentiators</h3>
              <ul className="space-y-3">
                {(a.differentiators || defaultDifferentiators).map((d) => (
                  <li key={d} className="flex items-start gap-3 text-sm text-slate-700">
                    <CheckCircle size={16} className="text-[#13A8BD] mt-0.5 flex-shrink-0" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="lg:col-span-1">
            <div className="border border-[#0B2545]/10 p-8 sticky top-28">
              <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-8">Standards We Support</h3>
              <div className="space-y-0">
                {standards.map((s) => (
                  <div key={s.name} data-testid={`standard-${s.name.toLowerCase().replace(/[\s\/]+/g, '-')}`} className="py-4 border-b border-[#0B2545]/10 last:border-b-0">
                    <div className="text-sm font-bold text-[#0B2545]" style={{ fontFamily: "Chivo, sans-serif" }}>{s.name}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{s.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ValuesSection({ a }) {
  const valueCards = [
    { icon: Shield, title: a.independence || "Independence", desc: a.independence_desc || defaultValues[0].desc },
    { icon: Users, title: a.excellence || "Excellence", desc: a.excellence_desc || defaultValues[1].desc },
    { icon: Award, title: a.integrity || "Integrity", desc: a.integrity_desc || defaultValues[2].desc },
    { icon: Eye, title: a.innovation || "Innovation", desc: a.innovation_desc || defaultValues[3].desc },
  ];

  return (
    <section className="py-20 lg:py-28 bg-[#F7F9FC] border-t border-[#0B2545]/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="mb-14">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-4">{a.approach_label || "Our Approach"}</p>
          <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#0B2545]" style={{ fontFamily: "Chivo, sans-serif" }}>{a.approach_title || "What Sets Us Apart"}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#0B2545]/10 border border-[#0B2545]/10">
          {valueCards.map((v) => (
            <div key={v.title} data-testid={`value-${v.title.toLowerCase()}`} className="p-8 lg:p-12 bg-white">
              <v.icon size={28} className="text-[#13A8BD] mb-5" strokeWidth={1.5} />
              <h3 className="text-base font-bold text-[#0B2545] mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>{v.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DisclaimerSection({ a }) {
  return (
    <section className="py-16 bg-white border-t border-[#0B2545]/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="border-l-4 border-[#13A8BD] pl-8 mb-12">
          <p className="text-xs text-slate-500 leading-relaxed italic">
            {a.disclaimer || "GACA International delivers certification services in partnership with internationally accredited certification bodies. All certifications are issued through our accredited partner network."}
          </p>
        </div>
        <div className="text-center">
          <Link to="/contact" data-testid="about-cta" className="inline-flex items-center gap-2 bg-[#13A8BD] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#0e8a9c] transition-colors">
            Apply for Certification <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  const { t } = useLang();
  const a = t?.about || {};
  return (
    <div className="page-transition pt-20" data-testid="about-page">
      <HeroSection a={a} />
      <MissionSection a={a} />
      <ValuesSection a={a} />
      <DisclaimerSection a={a} />
    </div>
  );
}
